import { useMemo, useState } from "react"
import { HStack, Spacer, Stack } from "@chakra-ui/react"
import { PaymentsApiError, submitManualPayment, getAccountChangeCheck } from "@/api"
import { Button, SelectField, Text, TextField } from "@/components"
import { defaultAccountTitle, ensurePaymentsClient, getApiErrorMessage } from "../utils"
import { BANK_COUNTRIES, US_COUNTRY_CODE, resolveBankCountryCode } from "../data/bankCountries"
import { AccountChangeConfirm, ACCOUNT_IN_PROCESS_TEXT, accountChangeSuccessText } from "./AccountChangeConfirm"

// Unified manual bank-entry form — replaces the old UsManualBankFormComponent /
// ForeignWireInformationFormComponent split (Bill, 2026-08-13).
//
// Why they merged: the two forms were auto-selected by client.country on the
// PAYMENTS server, which nothing populates until a payment is staged. Every new
// client therefore fell through to the US form, which had no SWIFT or IBAN field —
// so an international client had no way to enter their own bank details.
//
// Mode is driven by ONE question — "Where is your bank account held?" — asked as a
// country select, the same opening move Wise/Stripe/Payoneer use. It is deliberately
// NOT progressive disclosure behind a "my bank is foreign" link: the clients who hit
// the dead end are exactly the ones a hidden control would keep hiding it from, and
// an explicit up-front declaration is what stops an IBAN being typed into a field
// labelled "Account Number". US is preselected, so the majority case costs nothing.
//
// The country also fills bank_account.bank_country, which the DM Override page
// (routes/overrides.js) has always collected and the client form used to drop.
//
// Field set mirrors that same DM Override page, the reference the payments server
// validates against:
//   US            → 9-digit ABA routing + account number
//   International → SWIFT/BIC + (account number and/or IBAN) + correspondent ABA

interface Props {
  defaultClientData: {
    firstName: string
    lastName: string
    email: string
    companyName: string
    esoId?: number | null
    /** Legal name on the payments client record; the locked account title when set. */
    legalName?: string | null
  }
  dealEsoId?: number
  dealManagerEmail?: string
  paymentAmount: number
  paymentLabel: string
  /** Free-text country from the OG / payments client record; preselects the list. */
  detectedCountry?: string | null
  /**
   * Start with no country selected and hide the identifier fields until one is
   * picked. Used when we know the account is NOT domestic but not where it is —
   * defaulting such a client to US would contradict what they just told us.
   */
  promptForCountry?: boolean
  /**
   * "Receive Payments by Wire" (Bill, 2026-09-29): the client's connected account can
   * only be paid by ACH (a Plaid limitation), and they are entering their bank's
   * WIRE instructions. Wire-worded labels and guidance, and require_wire so the
   * server refuses a routing number that cannot receive a wire instead of saving
   * another ACH-only account. No memo field (Bill: not needed); instructions that
   * carry a memo / For Further Credit line are sent to the equity partner.
   */
  wireMode?: boolean
  onSuccess?: (message: string) => void
}

type FieldErrors = Record<string, string>

// Bank identifiers are written with spaces and dashes on statements ("CR05 0152 0200
// 1026 2840 66"). Everything downstream — the pain.001 <IBAN> element especially —
// needs them stripped, so normalise at the boundary rather than storing what was typed.
const compact = (v: string) => v.replace(/[\s-]/g, "")

export const ManualBankFormComponent = ({
  defaultClientData,
  dealEsoId,
  dealManagerEmail,
  paymentAmount,
  paymentLabel,
  detectedCountry,
  promptForCountry = false,
  wireMode = false,
  onSuccess,
}: Props) => {
  const [bankCountry, setBankCountry] = useState(() => {
    const resolved = resolveBankCountryCode(detectedCountry)
    if (resolved) return resolved
    return promptForCountry ? "" : US_COUNTRY_CODE
  })
  // The wire goes out under the account TITLE, and a $321.5k wire bounced because
  // an editable prefill let the client's short name ride through as the title.
  // So the holder name is locked to our record of the client, and a differently
  // titled account (joint, trust, business, another spelling) is an explicit
  // checkbox that expands a required title field — which the DM notification and
  // the approval email then flag for review (Bill, 2026-09-26). The locked name is
  // the legal name on file when there is one (Bill, 2026-09-29; defaultAccountTitle).
  const defaultTitle = defaultAccountTitle(defaultClientData)
  const [differentTitle, setDifferentTitle] = useState(false)
  const [accountTitle, setAccountTitle] = useState("")
  const accountHolderName = differentTitle ? accountTitle : defaultTitle.name
  const [bankName, setBankName] = useState("")
  const [accountType, setAccountType] = useState("checking")
  const [routingNumber, setRoutingNumber] = useState("")
  const [swiftCode, setSwiftCode] = useState("")
  const [iban, setIban] = useState("")
  const [accountNumber, setAccountNumber] = useState("")
  const [numberConfirm, setNumberConfirm] = useState("")
  const [correspondentAba, setCorrespondentAba] = useState("")
  const [intermediaryBank, setIntermediaryBank] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  // A payment waiting for approval that saving these details would cancel and send again
  // for approval to this account (Bill, 2026-10-13): the client confirms first.
  const [pendingChange, setPendingChange] = useState<{ amount: number; currency?: string } | null>(null)

  // Until a country is chosen we do not know which identifier set applies, so the
  // form shows neither rather than guessing one.
  const countryChosen = bankCountry !== ""
  const isInternational = countryChosen && bankCountry !== US_COUNTRY_CODE
  const isSaveOnly = !(paymentAmount > 0)

  const countryOptions = useMemo(() => {
    const options = BANK_COUNTRIES.map((c) => ({ value: c.code, label: c.name }))
    return countryChosen
      ? options
      : [{ value: "", label: "Select your bank's country…" }, ...options]
  }, [countryChosen])

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount)

  // Switching country clears the rail-specific identifiers. Without this a
  // half-filled US form could submit a routing number alongside a SWIFT code, and
  // the payments server treats routing+swift as domestic — silently ignoring the BIC.
  const changeCountry = (next: string) => {
    if (next === bankCountry) return
    const wasInternational = isInternational
    setBankCountry(next)
    if (wasInternational !== (next !== US_COUNTRY_CODE)) {
      setRoutingNumber("")
      setSwiftCode("")
      setIban("")
      setCorrespondentAba("")
      setNumberConfirm("")
    }
    setErrors({})
    setFormError("")
  }

  const validate = (): FieldErrors => {
    const next: FieldErrors = {}

    if (!countryChosen) {
      next.bankCountry = "Choose the country where your bank account is held."
      return next
    }

    if (differentTitle && !accountTitle.trim())
      next.accountTitle = "Enter the name exactly as it appears on the account."
    if (!bankName.trim()) next.bankName = "Enter your bank's name."

    if (isInternational) {
      const swift = compact(swiftCode)
      if (!swift) {
        next.swiftCode = "Enter your bank's SWIFT / BIC code."
      } else if (!/^[A-Z0-9]{8}([A-Z0-9]{3})?$/i.test(swift)) {
        next.swiftCode = "A SWIFT / BIC code is 8 or 11 letters and digits."
      }
      if (!compact(accountNumber) && !compact(iban)) {
        next.accountNumber = "Enter an account number or an IBAN."
        next.iban = "Enter an account number or an IBAN."
      }
      if (correspondentAba && !/^\d{9}$/.test(correspondentAba)) {
        next.correspondentAba = "A correspondent ABA is exactly 9 digits."
      }
    } else {
      if (!/^\d{9}$/.test(routingNumber)) {
        next.routingNumber = "A US routing number is exactly 9 digits."
      }
      if (!compact(accountNumber)) {
        next.accountNumber = "Enter your account number."
      }
    }

    // Retype guard, mirroring the DM Override page: a mistyped account number is the
    // most expensive error on this form (a returned or misdirected wire).
    // A client who supplies BOTH an account number and an IBAN may legitimately
    // re-type either one, so accept a match against whichever they confirmed.
    const candidates = [compact(accountNumber), compact(iban)].filter(Boolean)
    if (candidates.length && !candidates.includes(compact(numberConfirm))) {
      next.numberConfirm =
        candidates.length > 1
          ? "This does not match your account number or your IBAN."
          : "This does not match the number you entered above."
    }

    return next
  }

  // The payments server re-validates everything and is the authority — most notably
  // it hard-fails a SWIFT wire whose BIC is not in the curated correspondent table.
  // Pin that specific rejection to the field that fixes it rather than dropping a
  // wall of server text at the bottom of the form.
  const applyServerError = (error: unknown) => {
    // On a 400 the payments server answers {error: "Validation failed", details: [...]}
    // and the axios interceptor puts the ARRAY on .details, not the message — so
    // getApiErrorMessage alone yields a bare "Validation failed". Flatten both or the
    // client is told nothing about what to fix.
    const detailList =
      error instanceof PaymentsApiError && Array.isArray(error.details)
        ? (error.details as unknown[]).map(String)
        : []
    const text = [getApiErrorMessage(error), ...detailList].join(" ")
    // require_wire: the routing number cannot receive wires. Pin it to that field.
    if (/cannot receive wire transfers/i.test(text)) {
      setErrors((prev) => ({ ...prev, routingNumber: getApiErrorMessage(error) }))
      setFormError("")
      return
    }
    if (/correspondent_bank_aba/i.test(text)) {
      setErrors((prev) => ({
        ...prev,
        correspondentAba:
          "We could not look up your bank's US correspondent automatically. Please ask your bank for the US routing number (ABA) they receive USD wires through, and enter it here.",
      }))
      setFormError("")
      return
    }
    // Prefer the server's specific complaints over the generic envelope message.
    setFormError(detailList.length ? detailList.join(" ") : getApiErrorMessage(error))
  }

  // `confirmed`: the client has seen what the change does to their payment waiting for
  // approval (AccountChangeConfirm) and chose to continue.
  const submitBankDetails = async (confirmed = false) => {
    setFormError("")
    setSuccessMessage("")

    const found = validate()
    setErrors(found)
    if (Object.keys(found).length > 0) {
      setFormError("Please correct the highlighted fields.")
      return
    }

    setIsSubmitting(true)
    let clientIdForCheck = ""
    try {
      if (
        !defaultClientData.firstName ||
        !defaultClientData.lastName ||
        !defaultClientData.email ||
        !defaultClientData.companyName
      ) {
        throw new Error("Missing required client information (name, email, or company).")
      }

      const paymentsClient = await ensurePaymentsClient({
        first_name: defaultClientData.firstName,
        last_name: defaultClientData.lastName,
        email: defaultClientData.email,
        company_name: defaultClientData.companyName,
        client_number: String(defaultClientData.esoId || 0),
      })

      clientIdForCheck = paymentsClient.id
      // What the change does to a payment in flight, asked before anything is sent.
      // null (no answer): send as before; the payments server re-checks under lock.
      if (!confirmed) {
        const check = await getAccountChangeCheck(paymentsClient.id)
        if (check?.in_process) {
          setFormError(ACCOUNT_IN_PROCESS_TEXT)
          return
        }
        if (check?.waiting) {
          setPendingChange({ amount: check.waiting.amount, currency: check.waiting.currency })
          return
        }
      }

      const amount = paymentAmount > 0 ? paymentAmount : 0

      const result = await submitManualPayment({
        client_id: paymentsClient.id,
        deal_eso_id: dealEsoId,
        transfer_type: "WIRE",
        account_holder_name: accountHolderName.trim(),
        bank_name: bankName.trim(),
        amount,
        account_label: bankName.trim(),
        deal_manager_email: dealManagerEmail,
        bank_country: bankCountry,
        account_type: accountType,
        routing_number: isInternational ? undefined : routingNumber,
        swift_bic_code: isInternational ? compact(swiftCode).toUpperCase() : undefined,
        iban: isInternational && compact(iban) ? compact(iban).toUpperCase() : undefined,
        correspondent_bank_aba:
          isInternational && correspondentAba ? correspondentAba : undefined,
        account_number: compact(accountNumber) || undefined,
        notes: intermediaryBank.trim() ? `Intermediary Bank: ${intermediaryBank.trim()}` : undefined,
        // Zero-amount visits save the account without creating a transaction. The old
        // foreign form hard-required amount > 0, so international clients could not
        // register a bank account ahead of their first payment.
        save_only: amount > 0 ? undefined : true,
        require_wire: wireMode && !isInternational ? true : undefined,
        confirm_cancel_pending: confirmed ? true : undefined,
      })
      setPendingChange(null)

      // Clear sensitive fields immediately after a successful submission
      setRoutingNumber("")
      setAccountNumber("")
      setIban("")
      setNumberConfirm("")

      const msg = accountChangeSuccessText(wireMode ? "Your wire instructions have been saved." : "Your bank account has been saved.", result)
        ?? (wireMode
        ? amount > 0
          ? `Your wire instructions have been saved. Your payment of ${formatCurrency(amount)} will be sent by wire once it is approved. If you have any questions, please contact your equity partner.`
          : "Your wire instructions have been saved. Future payments will be sent to this account by wire."
        : amount > 0
          ? `Your bank account has been saved and payment of ${formatCurrency(amount)} has been submitted for ESO Fund approval. Please reach out to your equity partner if you have any questions.`
          : "Your bank account has been saved. You will be able to accept payments once they are available.")

      if (onSuccess) {
        onSuccess(msg)
      } else {
        setSuccessMessage(msg)
        setTimeout(() => window.location.reload(), 2000)
      }
    } catch (error) {
      setPendingChange(null)
      // A payment started waiting for approval after the check: ask now. The details
      // are still in the form, so Continue sends them again with the confirmation.
      if (error instanceof PaymentsApiError && error.code === "CONFIRM_CANCEL_PENDING" && clientIdForCheck) {
        const again = await getAccountChangeCheck(clientIdForCheck)
        if (again?.waiting) {
          setPendingChange({ amount: again.waiting.amount, currency: again.waiting.currency })
          return
        }
      }
      applyServerError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Stack w="100%" bg="white" gap={5}>
      {/* Amount context. Save-only visits have no payment staged yet, and showing a
          bold $0.00 there reads as an error to the client. */}
      <Stack p={5} borderWidth="1px" borderRadius="lg" borderColor="blue.200" bg="blue.50">
        <Text fontSize="sm" color="gray.600">{paymentLabel}</Text>
        {isSaveOnly ? (
          <Text fontSize="md" fontWeight="semibold">
            No payment is due yet. Add your account now and we will use it when one is ready.
          </Text>
        ) : (
          <Text fontSize="2xl" fontWeight="bold">{formatCurrency(paymentAmount)}</Text>
        )}
      </Stack>

      {wireMode ? (
        <Stack gap={3}>
          <Text fontSize="md" fontWeight="semibold">Wire Transfer Instructions</Text>
          <Text fontSize="sm" color="gray.600">
            Plaid could provide only the ACH details for your connected account. To receive your
            payments by wire, please enter your bank&rsquo;s wire instructions exactly as your bank
            provides them. They are typically available in your bank&rsquo;s app or website under Wire
            Transfers or Account Details. Your information is encrypted and stored securely.
          </Text>
          <Stack p={3} borderWidth="1px" borderRadius="md" borderColor="gray.200" bg="gray.50">
            <Text fontSize="sm" color="gray.700">
              If your bank&rsquo;s wire instructions include a memo or &ldquo;For Further Credit&rdquo;
              instruction, please contact your equity partner instead of submitting this form, so we can
              make sure the wire is credited to your account.
            </Text>
          </Stack>
        </Stack>
      ) : (
        <Text fontSize="sm" color="gray.600">
          Enter your details exactly as they appear on your bank statement. Your information is
          stored encrypted, and all payments are sent in US dollars.
        </Text>
      )}

      <SelectField
        label="Where Is Your Bank Account Held?"
        value={bankCountry}
        options={countryOptions}
        required
        error={errors.bankCountry}
        onChange={changeCountry}
        hint={
          isInternational
            ? "We will send an international wire in USD to this country."
            : "Choose your bank's country to see the right fields."
        }
      />

      {countryChosen && (
      <>
      <HStack gap={4} alignItems="flex-start">
        <TextField
          label="Account Holder Name"
          value={defaultTitle.name}
          hint={defaultTitle.isLegalName ? "Your legal name on file with us." : undefined}
          disabled
          onChange={() => {}}
        />
        <TextField
          label="Bank Name"
          placeholder={isInternational ? "Enter bank name" : "e.g. Chase, Bank of America"}
          value={bankName}
          required
          error={errors.bankName}
          onChange={setBankName}
        />
      </HStack>

      <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={differentTitle}
          onChange={(e) => setDifferentTitle(e.target.checked)}
          style={{ marginTop: "3px" }}
        />
        <Text fontSize="sm">
          {defaultTitle.isLegalName
            ? "This account is under a different name, such as a joint account, a trust or business title, or another spelling of your name"
            : "This account is under a different name, such as your full legal name, a joint account, or a trust or business title"}
        </Text>
      </label>

      {differentTitle && (
        <TextField
          label="Name on the Account"
          hint="Exactly as it appears on the bank account. Banks reject wires when the name differs, for example a short name in place of the full one, or a missing trust title."
          placeholder="Full name or title on the account"
          value={accountTitle}
          required
          error={errors.accountTitle}
          onChange={setAccountTitle}
        />
      )}

      <SelectField
        label="Account Type"
        value={accountType}
        options={[
          { value: "checking", label: "Checking" },
          { value: "savings", label: "Savings" },
        ]}
        required
        onChange={setAccountType}
      />

      {isInternational ? (
        <>
          <HStack gap={4} alignItems="flex-start">
            <TextField
              label="SWIFT / BIC Code"
              placeholder="8 or 11 characters"
              value={swiftCode}
              required
              autoComplete="off"
              error={errors.swiftCode}
              hint="Your bank's international identifier."
              onChange={(v) => setSwiftCode(v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11))}
            />
            <TextField
              label="Account Number"
              placeholder="Enter account number"
              value={accountNumber}
              autoComplete="off"
              error={errors.accountNumber}
              onChange={setAccountNumber}
            />
          </HStack>

          <TextField
            label="IBAN"
            placeholder="Enter IBAN"
            value={iban}
            autoComplete="off"
            error={errors.iban}
            hint="Provide an account number or an IBAN, or both if your bank gave you both."
            onChange={(v) => setIban(v.toUpperCase().slice(0, 42))}
          />

          <TextField
            label="US Correspondent Bank Routing Number"
            placeholder="9-digit routing number"
            value={correspondentAba}
            inputMode="numeric"
            autoComplete="off"
            error={errors.correspondentAba}
            hint="Optional. This is the US bank your bank receives USD through. We look it up automatically for most banks, so leave it blank unless your bank gave you one."
            onChange={(v) => setCorrespondentAba(v.replace(/\D/g, "").slice(0, 9))}
          />

          <TextField
            label="Intermediary Bank"
            placeholder="Enter intermediary bank details if applicable"
            value={intermediaryBank}
            hint="Optional. Any extra routing instructions your bank gave you for incoming USD wires. Our team reviews these by hand."
            onChange={setIntermediaryBank}
          />
        </>
      ) : (
        <>
          <HStack gap={4} alignItems="flex-start">
            <TextField
              label={wireMode ? "Wire Routing Number (ABA)" : "ABA Routing Number"}
              placeholder="9-digit routing number"
              value={routingNumber}
              required
              inputMode="numeric"
              autoComplete="off"
              error={errors.routingNumber}
              hint={wireMode ? "Use the wire routing number from your bank's wire instructions. It often differs from the routing number printed on your checks." : undefined}
              onChange={(v) => {
                setRoutingNumber(v.replace(/\D/g, "").slice(0, 9))
                // A server "cannot receive wire transfers" error is about the number they are now changing.
                if (errors.routingNumber) setErrors(({ routingNumber: _stale, ...rest }) => rest)
              }}
            />
            <TextField
              label="Account Number"
              placeholder="Enter account number"
              value={accountNumber}
              required
              autoComplete="off"
              error={errors.accountNumber}
              hint={wireMode ? "Enter the account number exactly as it appears in your wire instructions." : undefined}
              onChange={setAccountNumber}
            />
          </HStack>
        </>
      )}

      <TextField
        label={isInternational ? "Re-type Account Number or IBAN" : "Re-type Account Number"}
        placeholder="Type it again, pasting is disabled"
        value={numberConfirm}
        required
        autoComplete="off"
        error={errors.numberConfirm}
        hint="We ask twice because a single mistyped digit means a returned or misdirected wire."
        // Pasting the confirmation copies any typo along with it, which defeats the
        // entire point of asking twice.
        onPaste={(e) => e.preventDefault()}
        onChange={setNumberConfirm}
      />
      </>
      )}

      {formError ? (
        <Stack p={3} borderWidth="1px" borderRadius="md" borderColor="red.200" bg="red.50">
          <Text fontSize="sm" color="red.700">{formError}</Text>
        </Stack>
      ) : null}
      {successMessage ? (
        <Stack p={3} borderWidth="1px" borderRadius="md" borderColor="green.200" bg="green.50">
          <Text fontSize="sm" color="green.700">{successMessage}</Text>
        </Stack>
      ) : null}

      {pendingChange ? (
        <AccountChangeConfirm
          waiting={pendingChange}
          busy={isSubmitting}
          onContinue={() => { void submitBankDetails(true) }}
          onCancel={() => setPendingChange(null)}
        />
      ) : (
        <HStack>
          <Spacer />
          {/* Not onClick={submitBankDetails}: the click event would arrive as `confirmed`. */}
          <Button
            variant="solid"
            w="fit-content"
            mt={2}
            disabled={isSubmitting}
            onClick={() => { void submitBankDetails() }}
          >
            {isSubmitting ? "Submitting..." : wireMode ? (isSaveOnly ? "Save Wire Instructions" : "Submit Wire Instructions") : isSaveOnly ? "Save Bank Details" : "Submit Bank Details"}
          </Button>
        </HStack>
      )}
    </Stack>
  )
}
